import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { useQuery } from '@tanstack/react-query';
import {
  Box,
  Button,
  Container,
  Grid,
  Stack,
  Typography,
  Skeleton,
} from '@mui/material';
import ArrowForwardRoundedIcon from '@mui/icons-material/ArrowForwardRounded';
import { providersService } from '../../shared/services/providers.service';
import { ProviderCard } from '../../shared/components/ui/ProviderCard';
import { CategoryShowcase } from '../../shared/components/ui/CategoryShowcase';
import { InspirationMosaic } from '../../shared/components/ui/InspirationMosaic';
import { getEditorialAccent } from '../../shared/assets/categoryImages';
import { useSelectedCountry } from '../../shared/hooks/useSelectedCountry';
import { SenegalHero } from './SenegalHero';
import { HowItWorksSection } from './HowItWorksSection';
import type { SxProps, Theme } from '@mui/material/styles';

/**
 * Image with a curated primary URL and a local fallback. If the primary
 * (Unsplash) URL fails to load we swap to the local asset so the home page
 * collage never shows an empty frame.
 */
function AccentImg({
  primary,
  fallback,
  sx,
}: {
  primary: string;
  fallback: string;
  sx?: SxProps<Theme>;
}) {
  return (
    <Box
      component="img"
      src={primary || fallback}
      alt=""
      // Single-shot fallback. Without the dataset guard, Chrome on iOS keeps
      // firing onError each time we re-assign img.src (even when assigning
      // the same URL), and the previous "img.src !== fallback" check always
      // succeeds because the browser resolves img.src to an absolute URL
      // while fallback is a Vite-hashed relative path — recursion = stack
      // overflow = white screen at boot on iPhone Chrome.
      onError={(e) => {
        const img = e.currentTarget as HTMLImageElement;
        if (img.dataset.fellback === '1') return;
        img.dataset.fellback = '1';
        if (fallback && fallback !== primary) img.src = fallback;
      }}
      sx={sx}
    />
  );
}

export function HomePage() {
  const { t } = useTranslation();
  const navigate = useNavigate();

  // Scope "featured providers" to the visitor's selected market so a
  // French visitor lands on French shops, German on German shops. The
  // hook is the single source of truth across the app (top-bar dialog +
  // landing pages all share it via localStorage).
  const { countryCode: selectedCountry } = useSelectedCountry();
  // Senegal gets its own hero + "how it works" section because the
  // service mix (trades / home services) and the visitor mindset (find
  // a verified artisan) differ from the European beauty-event focus.
  // Everything else — the category grid and the featured providers
  // list — is shared and already country-aware on its own.
  const isSenegal = (selectedCountry || '').toUpperCase() === 'SN';
  const featured = useQuery({
    queryKey: ['providers', 'featured', selectedCountry],
    queryFn: () => providersService.search({
      countryCode: selectedCountry || undefined,
      page: 0,
      size: 6,
    }),
  });

  // One representative image per main service area so the hero collage
  // previews everything the app covers — not just hair & cake. Each
  // accent uses a curated Unsplash URL (see EDITORIAL_IMAGES) and falls
  // back to a local asset if the CDN is unreachable.
  const heroAccents = [
    getEditorialAccent('beautyHair', 1),          // Beauty & Hair
    getEditorialAccent('foodCatering', 0),        // Food & Catering
    getEditorialAccent('artistsPerformance', 0),  // Artists & Performance
    getEditorialAccent('fashionStyle', 0),        // Fashion & Style
    getEditorialAccent('eventServices', 0),       // Event Services
    getEditorialAccent('mediaPhotoAnimation', 0), // Media, Photo & Animation
  ].filter((a) => a.primary || a.fallback);

  return (
    <Box>
      {/* HERO — Senegal gets its own component; every other market uses
          the editorial European collage below. */}
      {isSenegal ? <SenegalHero /> : (
      <Box
        sx={{
          position: 'relative',
          overflow: 'hidden',
          py: { xs: 6, md: 9 },
          backgroundImage: (theme) =>
            theme.palette.mode === 'dark'
              ? `radial-gradient(60% 80% at 20% 0%, ${theme.palette.primary.dark}55 0%, transparent 60%),
                 radial-gradient(50% 80% at 90% 30%, ${theme.palette.secondary.dark}40 0%, transparent 60%)`
              : `radial-gradient(60% 80% at 20% 0%, ${theme.palette.primary.light}33 0%, transparent 60%),
                 radial-gradient(50% 80% at 90% 30%, ${theme.palette.secondary.light}26 0%, transparent 60%)`,
        }}
      >
        <Container maxWidth="lg" sx={{ position: 'relative' }}>
          <Grid container spacing={{ xs: 4, md: 6 }} alignItems="center">
            <Grid item xs={12} md={7}>
              <Typography
                variant="overline"
                sx={{
                  color: 'primary.main',
                  letterSpacing: '0.18em',
                  fontWeight: 700,
                  mb: 2,
                  display: 'inline-block',
                }}
              >
                {t('app.tagline')}
              </Typography>
              <Typography
                variant="h1"
                sx={{
                  mb: 2.5,
                  background: (theme) =>
                    `linear-gradient(135deg, ${theme.palette.primary.main} 0%, ${theme.palette.secondary.main} 100%)`,
                  WebkitBackgroundClip: 'text',
                  WebkitTextFillColor: 'transparent',
                  backgroundClip: 'text',
                }}
              >
                {t('home.heroTitle')}
              </Typography>
              <Typography
                variant="subtitle1"
                color="text.secondary"
                sx={{ fontSize: { xs: '1.0625rem', md: '1.25rem' }, lineHeight: 1.55, mb: 4 }}
              >
                {t('home.heroSubtitle')}
              </Typography>

              <Button
                variant="contained"
                color="primary"
                size="large"
                endIcon={<ArrowForwardRoundedIcon />}
                onClick={() => navigate('/search')}
                sx={{ px: 4, py: 1.5 }}
              >
                {t('home.browseCategories')}
              </Button>

              {/* HERO ACCENT STRIP — mobile only.  Horizontally scrollable
                  so we can preview every service area (beauty, food,
                  performance, fashion, event, media) without overflowing
                  the viewport.  The desktop collage in the next Grid item
                  remains hidden on xs/sm. */}
              <Box
                sx={{
                  display: { xs: 'block', md: 'none' },
                  mt: 4,
                  mx: -2, // bleed to the edge for a more immersive feel
                  px: 2,
                  overflowX: 'auto',
                  overflowY: 'hidden',
                  WebkitOverflowScrolling: 'touch',
                  scrollSnapType: 'x mandatory',
                  '&::-webkit-scrollbar': { display: 'none' },
                  scrollbarWidth: 'none',
                }}
              >
                <Stack
                  direction="row"
                  spacing={1.5}
                  sx={{ width: 'max-content', py: 1 }}
                >
                  {heroAccents.map((accent, i) => (
                    <Box key={i} sx={{ flexShrink: 0, scrollSnapAlign: 'start' }}>
                      <AccentImg
                        primary={accent.primary}
                        fallback={accent.fallback}
                        sx={{
                          width: { xs: 140, sm: 170 },
                          height: { xs: 170, sm: 200 },
                          objectFit: 'cover',
                          borderRadius: 3,
                          // Subtle alternating tilt for editorial feel.
                          transform:
                            i % 2 === 0
                              ? 'rotate(-2deg)'
                              : 'translateY(-6px) rotate(2deg)',
                          boxShadow: (theme) =>
                            theme.palette.mode === 'dark'
                              ? '0 12px 28px rgba(0,0,0,0.55)'
                              : '0 12px 28px rgba(26,24,22,0.16)',
                          border: '3px solid',
                          borderColor: 'background.default',
                        }}
                      />
                    </Box>
                  ))}
                </Stack>
              </Box>
            </Grid>

            {/* HERO ACCENT COLLAGE — desktop only.
                Five cards positioned absolutely across the right column to
                preview every main service area (Beauty, Food, Performance,
                Fashion, Event).  The 6th category (Media) is reachable from
                the CategoryShowcase grid below, kept out of the hero to
                avoid visual clutter. */}
            <Grid item md={5} sx={{ display: { xs: 'none', md: 'block' } }}>
              <Box
                sx={{
                  position: 'relative',
                  height: 500,
                  width: '100%',
                }}
              >
                {/* Hero card — Beauty & Hair, biggest, top-right anchor. */}
                {heroAccents[0] && (
                  <AccentImg
                    primary={heroAccents[0].primary}
                    fallback={heroAccents[0].fallback}
                    sx={{
                      position: 'absolute',
                      top: 0,
                      right: 24,
                      width: 230,
                      height: 290,
                      objectFit: 'cover',
                      borderRadius: 4,
                      boxShadow: (theme) =>
                        theme.palette.mode === 'dark'
                          ? '0 24px 60px rgba(0,0,0,0.6)'
                          : '0 24px 60px rgba(26,24,22,0.18)',
                      transform: 'rotate(3deg)',
                    }}
                  />
                )}
                {/* Food & Catering — bottom-left anchor. */}
                {heroAccents[1] && (
                  <AccentImg
                    primary={heroAccents[1].primary}
                    fallback={heroAccents[1].fallback}
                    sx={{
                      position: 'absolute',
                      bottom: 24,
                      left: 0,
                      width: 195,
                      height: 195,
                      objectFit: 'cover',
                      borderRadius: 4,
                      boxShadow: (theme) =>
                        theme.palette.mode === 'dark'
                          ? '0 20px 50px rgba(0,0,0,0.55)'
                          : '0 20px 50px rgba(26,24,22,0.16)',
                      transform: 'rotate(-5deg)',
                      border: '4px solid',
                      borderColor: 'background.default',
                    }}
                  />
                )}
                {/* Artists & Performance — bottom-right anchor. */}
                {heroAccents[2] && (
                  <AccentImg
                    primary={heroAccents[2].primary}
                    fallback={heroAccents[2].fallback}
                    sx={{
                      position: 'absolute',
                      bottom: 0,
                      right: 0,
                      width: 165,
                      height: 210,
                      objectFit: 'cover',
                      borderRadius: 4,
                      boxShadow: (theme) =>
                        theme.palette.mode === 'dark'
                          ? '0 20px 50px rgba(0,0,0,0.55)'
                          : '0 20px 50px rgba(26,24,22,0.16)',
                      transform: 'rotate(6deg)',
                      border: '4px solid',
                      borderColor: 'background.default',
                    }}
                  />
                )}
                {/* Fashion & Style — top-left, small accent. */}
                {heroAccents[3] && (
                  <AccentImg
                    primary={heroAccents[3].primary}
                    fallback={heroAccents[3].fallback}
                    sx={{
                      position: 'absolute',
                      top: 36,
                      left: 8,
                      width: 140,
                      height: 175,
                      objectFit: 'cover',
                      borderRadius: 3,
                      boxShadow: (theme) =>
                        theme.palette.mode === 'dark'
                          ? '0 16px 40px rgba(0,0,0,0.5)'
                          : '0 16px 40px rgba(26,24,22,0.14)',
                      transform: 'rotate(-4deg)',
                      border: '4px solid',
                      borderColor: 'background.default',
                    }}
                  />
                )}
                {/* Event Services — centre-mid, ties the collage together. */}
                {heroAccents[4] && (
                  <AccentImg
                    primary={heroAccents[4].primary}
                    fallback={heroAccents[4].fallback}
                    sx={{
                      position: 'absolute',
                      top: 240,
                      left: '38%',
                      width: 150,
                      height: 150,
                      objectFit: 'cover',
                      borderRadius: 3,
                      boxShadow: (theme) =>
                        theme.palette.mode === 'dark'
                          ? '0 16px 40px rgba(0,0,0,0.55)'
                          : '0 16px 40px rgba(26,24,22,0.14)',
                      transform: 'rotate(2deg)',
                      border: '4px solid',
                      borderColor: 'background.default',
                    }}
                  />
                )}
              </Box>
            </Grid>
          </Grid>
        </Container>
      </Box>
      )}

      {/* CATEGORIES — image-rich. Title + hint switch to a service-led
          copy on Senegal so the framing matches the trades / home-service
          taxonomy that the grid below renders. */}
      <Container maxWidth="lg" sx={{ py: { xs: 5, md: 8 } }}>
        <Stack direction="row" alignItems="baseline" sx={{ mb: 3 }}>
          <Box sx={{ flex: 1 }}>
            <Typography variant="h3" sx={{ mb: 0.75 }}>
              {t(isSenegal ? 'senegalHome.browseTitle' : 'home.browseCategories')}
            </Typography>
            <Typography color="text.secondary" sx={{ fontSize: '1.0625rem' }}>
              {t(isSenegal ? 'senegalHome.browseHint' : 'home.browseCategoriesHint')}
            </Typography>
          </Box>
          <Button
            endIcon={<ArrowForwardRoundedIcon />}
            onClick={() => navigate('/search')}
            sx={{ color: 'text.secondary', display: { xs: 'none', sm: 'inline-flex' } }}
          >
            {t('common.viewAll')}
          </Button>
        </Stack>
        <CategoryShowcase />
      </Container>

      {/* HOW IT WORKS — three-step trust-building band shown on every
          market. Sits right after the category grid so visitors who
          aren't sure how to engage with a provider see the path
          (Choose → Contact → Book) before scrolling further. */}
      <HowItWorksSection />

      {/* INSPIRATION MOSAIC — kept on the European markets only. The
          mosaic celebrates beauty / event imagery which fits the
          European taxonomy; on Senegal it would be off-topic next to the
          trade / home-service categories so we skip it there. */}
      {!isSenegal && (
      <Box sx={{ bgcolor: 'background.paper', py: { xs: 5, md: 8 } }}>
        <Container maxWidth="lg">
          <Box sx={{ textAlign: 'center', mb: { xs: 3, md: 5 } }}>
            <Typography variant="overline" color="primary.main" sx={{ letterSpacing: '0.18em', fontWeight: 700 }}>
              {t('home.inspirationLabel')}
            </Typography>
            <Typography variant="h3" sx={{ mt: 1, mb: 1 }}>
              {t('home.inspirationTitle')}
            </Typography>
            <Typography
              color="text.secondary"
              sx={{ fontSize: '1.0625rem', maxWidth: 560, mx: 'auto' }}
            >
              {t('home.inspirationHint')}
            </Typography>
          </Box>
          <InspirationMosaic />
        </Container>
      </Box>
      )}

      {/* FEATURED PROVIDERS — country-scoped on its own through the
          `selectedCountry` queryKey above. */}
      <Container maxWidth="lg" sx={{ py: { xs: 5, md: 8 } }}>
        <Stack direction="row" alignItems="baseline" sx={{ mb: 3 }}>
          <Typography variant="h3" sx={{ flex: 1 }}>
            {t(isSenegal ? 'senegalHome.featuredTitle' : 'home.featured')}
          </Typography>
          <Button
            endIcon={<ArrowForwardRoundedIcon />}
            onClick={() => navigate('/search')}
            sx={{ color: 'text.secondary' }}
          >
            {t('common.viewAll')}
          </Button>
        </Stack>

        <Grid container spacing={3}>
          {featured.isLoading
            ? Array.from({ length: 6 }).map((_, i) => (
                <Grid item xs={12} sm={6} md={4} key={i}>
                  <Skeleton variant="rounded" height={320} sx={{ borderRadius: 3 }} />
                </Grid>
              ))
            : (featured.data?.data ?? []).map((p) => (
                <Grid item xs={12} sm={6} md={4} key={p.id}>
                  <ProviderCard provider={p} />
                </Grid>
              ))}
        </Grid>

        {!featured.isLoading && (featured.data?.data?.length ?? 0) === 0 && (
          <Box sx={{ textAlign: 'center', py: 6 }}>
            <Typography color="text.secondary">{t('search.empty')}</Typography>
          </Box>
        )}
      </Container>
    </Box>
  );
}
